import React from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AppLayout } from "@/components/layout/AppLayout";
import { PublicLayout } from "@/components/layout/PublicLayout";

import LoginPage from "@/features/auth/LoginPage";
import ForgotPasswordPage from "@/features/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/features/auth/ResetPasswordPage";
import DashboardPage from "@/features/dashboard/DashboardPage";
import UsersListPage from "@/features/users/UsersListPage";
import UserFormPage from "@/features/users/UserFormPage";
import CustomersListPage from "@/features/customers/CustomersListPage";
import CustomerFormPage from "@/features/customers/CustomerFormPage";
import TechniciansListPage from "@/features/technicians/TechniciansListPage";
import TechnicianFormPage from "@/features/technicians/TechnicianFormPage";
import AssetsListPage from "@/features/assets/AssetsListPage";
import AssetFormPage from "@/features/assets/AssetFormPage";
import AssetDetailsPage from "@/features/assets/AssetDetailsPage";
import PublicAssetPage from "@/features/assets/PublicAssetPage";
import WorkOrdersListPage from "@/features/work_orders/WorkOrdersListPage";
import WorkOrderFormPage from "@/features/work_orders/WorkOrderFormPage";
import WorkOrderDetailsPage from "@/features/work_orders/WorkOrderDetailsPage";
import WorkshopListPage from "@/features/workshop/WorkshopListPage";
import WorkshopDetailsPage from "@/features/workshop/WorkshopDetailsPage";
import ProposalListPage from "@/features/proposals/ProposalListPage";
import ProposalNewPage from "@/features/proposals/ProposalNewPage";
import ProposalDetailsPage from "@/features/proposals/ProposalDetailsPage";
import FinanceDashboardPage from "@/features/finance/FinanceDashboardPage";
import AccountsListPage from "@/features/finance/AccountsListPage";
import AccountDetailsPage from "@/features/finance/AccountDetailsPage";
import SuppliersListPage from "@/features/finance/SuppliersListPage";
import CompanySettingsPage from "@/features/settings/CompanySettingsPage";
import ProfilePage from "@/features/profile/ProfilePage";
import SettingsPage from "@/features/settings/SettingsPage";
import NotFoundPage from "@/features/NotFoundPage";

import "./App.css";

const queryClient = new QueryClient({
    defaultOptions: {
        queries: { refetchOnWindowFocus: false, staleTime: 30_000, retry: 1 },
    },
});

function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <ThemeProvider>
                <AuthProvider>
                    <BrowserRouter>
                        <Routes>
                            <Route element={<PublicLayout />}>
                                <Route path="/login" element={<LoginPage />} />
                                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                                <Route path="/reset-password" element={<ResetPasswordPage />} />
                                <Route path="/public/assets/:id" element={<PublicAssetPage />} />
                            </Route>

                            <Route
                                element={
                                    <ProtectedRoute>
                                        <AppLayout />
                                    </ProtectedRoute>
                                }
                            >
                                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                                <Route path="/dashboard" element={<DashboardPage />} />

                                <Route path="/users" element={<UsersListPage />} />
                                <Route path="/users/new" element={<UserFormPage />} />
                                <Route path="/users/:id" element={<UserFormPage />} />

                                <Route path="/customers" element={<CustomersListPage />} />
                                <Route path="/customers/new" element={<CustomerFormPage />} />
                                <Route path="/customers/:id" element={<CustomerFormPage />} />

                                <Route path="/technicians" element={<TechniciansListPage />} />
                                <Route path="/technicians/new" element={<TechnicianFormPage />} />
                                <Route path="/technicians/:id" element={<TechnicianFormPage />} />

                                <Route path="/assets" element={<AssetsListPage />} />
                                <Route path="/assets/new" element={<AssetFormPage />} />
                                <Route path="/assets/:id" element={<AssetDetailsPage />} />
                                <Route path="/assets/:id/edit" element={<AssetFormPage />} />

                                <Route path="/work-orders" element={<WorkOrdersListPage />} />
                                <Route path="/work-orders/new" element={<WorkOrderFormPage />} />
                                <Route path="/work-orders/:id" element={<WorkOrderDetailsPage />} />
                                <Route path="/work-orders/:id/edit" element={<WorkOrderFormPage />} />

                                <Route path="/workshop" element={<WorkshopListPage />} />
                                <Route path="/workshop/:id" element={<WorkshopDetailsPage />} />

                                <Route path="/proposals" element={<ProposalListPage />} />
                                <Route path="/proposals/new" element={<ProposalNewPage />} />
                                <Route path="/proposals/:id" element={<ProposalDetailsPage />} />

                                <Route path="/finance" element={<FinanceDashboardPage />} />
                                <Route path="/finance/receivables" element={<AccountsListPage type="receivable" />} />
                                <Route path="/finance/receivables/:id" element={<AccountDetailsPage />} />
                                <Route path="/finance/payables" element={<AccountsListPage type="payable" />} />
                                <Route path="/finance/payables/:id" element={<AccountDetailsPage />} />
                                <Route path="/finance/suppliers" element={<SuppliersListPage />} />

                                <Route path="/settings/company" element={<CompanySettingsPage />} />

                                <Route path="/profile" element={<ProfilePage />} />
                                <Route path="/settings" element={<SettingsPage />} />
                            </Route>

                            <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                        <Toaster position="bottom-right" richColors closeButton />
                    </BrowserRouter>
                </AuthProvider>
            </ThemeProvider>
        </QueryClientProvider>
    );
}

export default App;
